


export const sendRequest = async <ResponseData>(
    params:
        | {
        url: string
        method: string
        body?: Record<string, unknown> | FormData
    }
        | string
): Promise<{ data?: ResponseData; error?: Error }> => {
    try {
        const url = typeof params === 'string' ? params : params.url
        const response = await fetch(url, {
            method: typeof params === 'string' ? 'GET' : params.method,
            mode: 'cors',
            headers:
                typeof params !== 'string' && isDefined(params.body)
                    ? {
                        'Content-Type': 'application/json',
                    }
                    : undefined,
            body:
                typeof params !== 'string' && isDefined(params.body)
                    ? JSON.stringify(params.body)
                    : undefined,
        })
        const data = await response.json()
        if (!response.ok) throw 'error' in data ? data.error : data
        return {data}
    } catch (e) {
        console.error(e)
        return {error: e as Error}
    }
}

export const isDefined = <T>(
    value: T | undefined | null
): value is NonNullable<T> => value !== undefined && value !== null

export const isNotDefined = <T>(
    value: T | undefined | null
): value is undefined | null => value === undefined || value === null

export const isEmpty = (
    value: string | undefined | null
): value is undefined | null =>
    value === undefined || value === null || value === ''


export const byId = (id?: string) => (obj: { id: string }) => obj.id === id

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

interface Omit {
    // eslint-disable-next-line @typescript-eslint/ban-types
    <T extends object, K extends [...(keyof T)[]]>(obj: T, ...keys: K): {
        [K2 in Exclude<keyof T, K[number]>]: T[K2]
    }
}

export const omit: Omit = (obj, ...keys) => {
    const ret = {} as {
        [K in keyof typeof obj]: (typeof obj)[K]
    }
    let key: keyof typeof obj
    for (key in obj) {
        if (!keys.includes(key)) {
            ret[key] = obj[key]
        }
    }
    return ret
}

const isVariableString = (str: string): boolean => /^\{\{.*\}\}$/.test(str)

export const sanitizeUrl = (url: string): string =>
    url.startsWith('http') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:') ||
    url.startsWith('sms:') ||
    isVariableString(url)
        ? url
        : `https://${url}`

export const toTitleCase = (str: string) =>
    str.replace(
        /\w\S*/g,
        (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
    )

export const generateId = (idDesiredLength: number): string => {
    const getRandomCharFromAlphabet = (alphabet: string): string => {
        return alphabet.charAt(Math.floor(Math.random() * alphabet.length))
    }

    return Array.from({length: idDesiredLength})
        .map(() => {
            return getRandomCharFromAlphabet(
                'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
            )
        })
        .join('')
}

export const hasValue = (
    value: string | undefined | null
): value is NonNullable<string> =>
    value !== undefined &&
    value !== null &&
    value !== '' &&
    value !== 'undefined' &&
    value !== 'null'

export const parseNumberWithCommas = (num: number) =>
    num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export const injectCustomHeadCode = (customHeadCode: string) => {
    const headCodes = customHeadCode.split('</noscript>')
    headCodes.forEach((headCode) => {
        const [codeToInject, noScriptContentToInject] = headCode.split('<noscript>')
        const fragment = document
            .createRange()
            .createContextualFragment(codeToInject)
        document.head.append(fragment)

        if (isNotDefined(noScriptContentToInject)) return

        const noScriptElement = document.createElement('noscript')
        const noScriptContentFragment = document
            .createRange()
            .createContextualFragment(noScriptContentToInject)
        noScriptElement.append(noScriptContentFragment)
        document.head.append(noScriptElement)
    })
}

export const getAtPath = <T>(obj: T, path: string): unknown => {
    if (isNotDefined(obj)) return undefined
    const pathParts = path.split('.')

    let current: any = obj
    for (const part of pathParts) {
        if (current === undefined) {
            return undefined
        }
        current = current[part]
    }
    return current
}

export function isBeijingMidnight(): boolean {
    const now = new Date();
    const utcHours = now.getUTCHours();
    const beijingHours = (utcHours + 8) % 24;
    return beijingHours === 0;
}

const holidays = ["2025-05-31", "2025-06-01", "2025-06-02", "2025-10-01", "2025-10-02", "2025-10-03", "2025-10-04", "2025-10-05", "2025-10-06", "2025-10-07", "2025-10-08"]

export function calculateCurrentDay(createdAt: Date): number {
    const holidaySet = new Set(holidays)
    const now = new Date();
    let count = 0
    const currentDate = new Date(createdAt)
    currentDate.setDate(currentDate.getDate() + 1);
    now.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    // 遍历从下单日后一天到当前日期（包含当前日期）的范围
    while (currentDate <= now) {
        // 检查是否是工作日 (周一到周五)
        const isWeekday = currentDate.getDay() >= 1 && currentDate.getDay() <= 5; // 1=周一 到 5=周五

        // 检查是否是假期
        const isHoliday = holidaySet.has(formatDateToString(currentDate));

        // 如果是工作日且不是假期，则计为一个交易日
        if (isWeekday && !isHoliday) {
            count++;
        }

        // 移动到下一天
        currentDate.setDate(currentDate.getDate() + 1);
    }

    // 根据需求，下单日后的第一个交易日是第 2 天，第二个是第 3 天...
    // 所以返回的交易日天数是计算出的交易日数量 + 1
    return count + 1;
}

function formatDateToString(date: Date): string {
    return [
        date.getFullYear(),
        (date.getMonth() + 1).toString().padStart(2, '0'),
        date.getDate().toString().padStart(2, '0')
    ].join('-')
}
